import i18n from '@risksmart-app/i18n/src/i18n';
import type { OversightRegisterResponse } from '@risksmart-app/trpc/src/types';
import {
  GetOversightAssessmentsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';

import { oversightAssessmentRegisterUrl } from '@/utils/urls';

import { useGetAssessmentSmartWidgetTableProps } from '../../../assessments/oversight-assessments/config';
import type { OversightAssessmentRegisterFields } from '../../../assessments/oversight-assessments/types';
import { UNRATED } from '../../gigawidget/types';
import {
  dateRangeFilter,
  departmentsFilter,
  tagsFilter,
} from '../../gigawidget/util/filterHelpers';
import { getQueryVariables } from '../../my-items/hooks/useGetQueryVariables';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';
import { mapTrpcOversightToGraphQL } from './oversight.mappers';

const data = createDataSource({
  group: 'single',
  parentTypes: [Parent_Type_Enum.OversightAssessment],
  hasAccess: (_, isModuleEnabled) =>
    isModuleEnabled('assessment.subModules.oversight_register_and_findings'),
  documentNode: GetOversightAssessmentsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.oversight.register.queryOptions();

    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: OversightRegisterResponse) => {
        return mapTrpcOversightToGraphQL(data);
      },
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({}),
  useTablePropsHook: (data, options) =>
    useGetAssessmentSmartWidgetTableProps(data?.oversight_assessment, options),
  entityNamePlural: 'compliance_monitoring_assessment_other',
  entityNameSingular: 'compliance_monitoring_assessment_one',
  fields: 'complianceMonitoringAssessment.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ where: { tags: tagsFilter(tags) } }),
    departmentsFilter: (departments) => ({
      where: { departments: departmentsFilter(departments) },
    }),
    dateFilter: (dateRange, precision) => ({
      where: { CreatedAtTimestamp: dateRangeFilter(dateRange, precision) },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters, userId) => ({
      where: getQueryVariables(myItemsFilters, userId)
        .assessmentFilterConditions,
    }),
  },
  clickThroughUrl: (filters) =>
    oversightAssessmentRegisterUrl(
      filters as Parameters<typeof oversightAssessmentRegisterUrl>[0]
    ),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<OversightAssessmentRegisterFields>(),
    {
      id: 'status',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Status'),
      categoryGetter: (item) => ({
        key: item.Status,
        label: item.StatusLabelled,
      }),
      ratingColourKey: 'assessment_status',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
    },
    {
      id: 'outcome',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Outcome'),
      categoryGetter: (item) => ({
        key: item.Outcome ?? UNRATED,
        label: item.OutcomeLabelled,
      }),
      ratingColourKey: 'compliance_monitoring_assessment_outcome',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('OutcomeLabelled'),
    },
    {
      id: 'owner',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      name: () => i18n.t('columns.contributors'),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'completionDate',
      name: () =>
        i18n.t('complianceMonitoringAssessment.columns.CompletionDate'),
      categoryGetter: (data) =>
        data.ActualCompletionDate ? new Date(data.ActualCompletionDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: {
          ActualCompletionDate: dateRangeFilter(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActualCompletionDate'),
    },
    {
      id: 'startDate',
      name: () => i18n.t('complianceMonitoringAssessment.columns.StartDate'),
      categoryGetter: (data) =>
        data.StartDate ? new Date(data.StartDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { StartDate: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('StartDate'),
    },
    {
      id: 'targetCompletionDate',
      name: () =>
        i18n.t('complianceMonitoringAssessment.columns.TargetCompletionDate'),
      categoryGetter: (data) =>
        data.TargetCompletionDate
          ? new Date(data.TargetCompletionDate as string)
          : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: {
          TargetCompletionDate: dateRangeFilter(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('TargetCompletionDate'),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) =>
        data.CreatedAtTimestamp ? new Date(data.CreatedAtTimestamp) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: {
          CreatedAtTimestamp: dateRangeFilter(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});

export default data;
